/**
 * "Shared with me recently" (#2303): the models shared with the viewer, the
 * latest updated first, drawn with the same model cards as every model
 * section. "See all" opens the same search in Discover's results.
 *
 * It is the advanced search's shared-with-me (`POST api/models/query`,
 * `shared: "me"`, #2289), as the server reads it since #2303: someone else's
 * model whose READ names the viewer, or a group they inherit other than
 * Everyone. Never the viewer's own (not even one shared with their own group),
 * nor a public, reserved or system one. The time a model was shared is not
 * stored, so "recently" is its last update.
 *
 * One request, `count` models, once the section comes near the viewport
 * (`useNearLoad`); a hidden section is not drawn and asks nothing.
 */
import { useCallback } from 'react';
import { Link } from 'react-router';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons';
import { queryModels, MODEL_QUERY_VERSION, type ModelQuery } from '@/api/modelQuery';
import type { DiscoverLayout, DiscoverSection } from '@/api/discoverPreferences';
import { ItemsView, SectionHead, SectionMessage, SectionSkeleton } from '../DiscoverCards';
import type { DiscoverItem } from '../useDiscoverData';
import { countOf, variantOf } from '../saved/discoverLayout';
import { DISCOVER_PATH, queryToSearch } from '../search/searchUrl';
import { EMPTY_QUERY } from '../search/searchSyntax';
import { useNearLoad } from './useNearLoad';

/** What the section asks: shared with the viewer, the latest updated first. */
const SHARED_WITH_ME_QUERY: ModelQuery = { v: MODEL_QUERY_VERSION, shared: 'me', sort: 'updated' };

const SEE_ALL = queryToSearch({ ...EMPTY_QUERY, shared: 'me', sort: 'updated' });

export interface SharedWithMeSectionProps {
  section: DiscoverSection;
  layout?: DiscoverLayout;
  navigate: (path: string) => void;
  className?: string;
}

export default function SharedWithMeSection({ section, layout, navigate, className = 'mb-9' }: Readonly<SharedWithMeSectionProps>) {
  const variant = variantOf(section, layout);
  const count = countOf(section, layout);
  const load = useCallback(
    () => queryModels(SHARED_WITH_ME_QUERY, { limit: count }).then((page) => page.data as unknown as DiscoverItem[]),
    [count],
  );
  const { ref, status, value, retry } = useNearLoad<DiscoverItem[]>(load);
  const items = value ?? [];

  const renderBody = () => {
    if (status === 'loading') return <SectionSkeleton variant={variant}/>;
    if (status === 'error') {
      return <SectionMessage onRetry={retry}>Couldn't load the models shared with you.</SectionMessage>;
    }
    if (items.length === 0) {
      return <SectionMessage>Models your colleagues share with you will show up here.</SectionMessage>;
    }
    return <ItemsView variant={variant} items={items} navigate={navigate}/>;
  };

  return (
    <section
      ref={ref}
      className={`${className}${variant === 'carousel' ? ' is-shelf' : ''}`}
      aria-label="Shared with me"
      aria-busy={status === 'loading'}
      data-section={section.id}
    >
      <SectionHead
        icon={faShareNodes}
        kicker="Shared with me"
        title="Shared with you recently"
        actions={<Link className="edit-section-link" to={{ pathname: DISCOVER_PATH, search: SEE_ALL }}>See all</Link>}
      />
      {renderBody()}
    </section>
  );
}
